import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../appStore';
import ContentConstants from '../../contentConstants';
import { ActionType, AudienceSourceType, ConditionSourceType, DisplayLogicConditionError } from '../../models';
import { addNewRule, getErrorCondition, selectDisplayLogicQuestions } from '../../store/QuestionBranchingLogicSlice';
import { selectSurveyQuestion } from '../../store/SurveyStore';
import { assertNonNullable } from '../../utilities/assertions';
import { useBranchingLogicAttributes, useBranchingLogicHierarchies } from '../../utilities/hooks';
import { LoadingIndicator } from '../common';
import { AddConditionsDropDown, DisabledReason, IDisabledReason } from '../displayLogic/AddConditionsDropDown';
import { BranchingLogicRuleAction } from './BranchingLogicRuleAction';
import { QuestionBranchingLogicScope } from './QuestionBranchingLogicScope';

const NewBranchingLogicContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	gap: 1rem;
	border: dashed 1px ${globalVars.tertiaryGray1};
	border-radius: 4px;
	background: ${globalVars.tertiaryGray3};
	padding: 1rem;
`;
NewBranchingLogicContainer.displayName = 'NewBranchingLogicContainer';

export const NewBranchingLogicRule = ({ questionId, scope }: { questionId?: number; scope: QuestionBranchingLogicScope }) => {
	const [actionType, setActionType] = useState(ActionType.ShowQuestion);
	const [destinationQuestionId, setDestinationQuestionId] = useState<number | undefined>(undefined);

	const dispatch = useAppDispatch();

	const { loading: audiencesLoading } = useAppSelector((state) => state.audienceList);
	const {
		audienceSource: { audienceSourceType },
	} = useAppSelector((state) => state.survey.surveyEditModel);
	const { attributes } = useBranchingLogicAttributes();
	const { selectableHierarchies } = useBranchingLogicHierarchies();

	const question = useAppSelector((state) => selectSurveyQuestion(state, questionId));
	const questionDisplayOrder = question?.displayOrder ?? 1;
	const maxDisplayOrder = actionType === ActionType.BranchToQuestion ? questionDisplayOrder + 1 : questionDisplayOrder;
	const displayLogicQuestions = useAppSelector((state) =>
		selectDisplayLogicQuestions(state, {
			maxDisplayOrder,
			hasResponseOptions: true,
		}),
	);

	const hasNoDataSource = getErrorCondition(audienceSourceType) === DisplayLogicConditionError.NoSourceSelected;
	const disabled = !questionId || !question || !question.displayOrder || hasNoDataSource;

	const attributeDataCouldNotBeFetched: DisabledReason = {
		disabled:
			!attributes ||
			(!attributes.isLoading && attributes.couldNotFetchData) ||
			(attributes.isLoading === undefined && attributes.length === 0),
		reason: audienceSourceType === AudienceSourceType.FileUpload ? IDisabledReason.NoDistribution : IDisabledReason.WrongAudienceSourceType,
	};
	const hierarchyDataCouldNotBeFetched: DisabledReason = {
		disabled:
			audienceSourceType === AudienceSourceType.FileUpload ||
			(selectableHierarchies?.isLoading === false && selectableHierarchies.couldNotFetchData) ||
			false,
		reason: audienceSourceType === AudienceSourceType.FileUpload ? IDisabledReason.WrongAudienceSourceType : undefined,
	};

	function handleActionTypeChange(value: ActionType) {
		setActionType(value);
	}

	function handleDestinationQuestionChange(value?: number) {
		setDestinationQuestionId(value);
	}

	function handleConditionMenuItemClick(conditionSourceType: ConditionSourceType) {
		assertNonNullable(questionId);
		dispatch(
			addNewRule({
				conditionSourceType,
				questionId,
				actionType,
				destinationQuestionId,
			}),
		);
		reset();
	}

	function reset() {
		setActionType(ActionType.ShowQuestion);
		setDestinationQuestionId(undefined);
	}

	return audiencesLoading || attributes?.isLoading || selectableHierarchies?.isLoading ? (
		<LoadingIndicator />
	) : (
		<NewBranchingLogicContainer className='tws-new-branching-rule' role='group' aria-label='New Branching Logic Rule'>
			<p>{ContentConstants.NEW_DISPLAY_LOGIC_RULE}</p>
			<div className='row w-100'>
				<div className='col'>
					<BranchingLogicRuleAction
						newRule
						actionType={actionType}
						disabled={disabled}
						destinationQuestionId={destinationQuestionId}
						questionDisplayOrder={question?.displayOrder || 0}
						scope={scope}
						onActionTypeChange={handleActionTypeChange}
						onDestinationQuestionChange={handleDestinationQuestionChange}
					/>
				</div>
			</div>
			<div className='row'>
				<div className='col-auto'>
					<AddConditionsDropDown
						disabled={disabled}
						audienceSourceType={audienceSourceType}
						noAvailableDisplayLogicQuestions={displayLogicQuestions.length < 1}
						attributeDisabled={attributeDataCouldNotBeFetched}
						hierarchyDisabled={hierarchyDataCouldNotBeFetched}
						actionType={actionType}
						onConditionMenuItemClick={handleConditionMenuItemClick}
					/>
				</div>
			</div>
		</NewBranchingLogicContainer>
	);
};
